import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, AlertCircle } from 'lucide-react'
import { useCurrentUser } from '@/features/auth'
import { DashboardLayout, SEO } from '@/shared/components'
import { useLocalizedNavigate } from '@/shared/hooks'
import { EmptyState, NewProjectCard, InProgressSection, CompletedSection, QuotaBar } from '../components'
import { useTeams } from '../hooks'
import type { TeamWithActions, ProjectStatus } from '@/shared/types'

// Get effective project status
const getProjectStatus = (team: TeamWithActions): ProjectStatus | null => {
  return team.active_project?.status || null
}

export function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { user: profile, canCreateProject } = useCurrentUser()
  const { data: teams, isLoading, error } = useTeams()

  // Get first name for welcome message
  const firstName = useMemo(() => {
    if (profile?.full_name) {
      return profile.full_name.split(' ')[0]
    }
    return null
  }, [profile])

  // Split teams into sections by status
  const { inProgressTeams, completedTeams } = useMemo(() => {
    if (!teams) return { inProgressTeams: [], completedTeams: [] }

    const inProgress: TeamWithActions[] = []
    const completed: TeamWithActions[] = []

    // Sort all teams by last activity first
    const sorted = [...teams].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    )

    for (const team of sorted) {
      const status = getProjectStatus(team)
      if (status === 'scoping' || status === 'pending') {
        inProgress.push(team)
      } else if (status === 'completed') {
        completed.push(team)
      } else {
        // Other statuses (active, paused, quoting) go to in progress
        inProgress.push(team)
      }
    }

    return { inProgressTeams: inProgress, completedTeams: completed }
  }, [teams])

  // Get primary team's subscription info (first team with a subscription)
  const subscriptionInfo = useMemo(() => {
    if (!teams) return null
    const teamWithSub = teams.find((t) => t.subscription_plan)
    if (!teamWithSub) return null
    return {
      plan: teamWithSub.subscription_plan,
      projectsUsed: teamWithSub.projects_this_month,
    }
  }, [teams])

  const handleCreateTeam = () => {
    navigate('/projects/new')
  }

  const hasTeams = teams && teams.length > 0

  return (
    <DashboardLayout>
      <SEO titleKey="seo.dashboard.title" noIndex />
      <div className="p-8">
        {/* Page header with welcome message */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">
            {firstName
              ? t('dashboard.welcomeBack', { name: firstName })
              : t('dashboard.title')}
          </h1>
          <p className="mt-1 text-foreground-muted">
            {t('dashboard.subtitle')}
          </p>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-foreground-muted" />
          </div>
        ) : error ? (
          <div className="flex h-64 flex-col items-center justify-center gap-2 text-destructive">
            <AlertCircle className="h-8 w-8" />
            <p>{t('common.error')}</p>
          </div>
        ) : !hasTeams ? (
          <EmptyState canCreateProject={canCreateProject} onCreateTeam={handleCreateTeam} />
        ) : (
          <div className="flex flex-col gap-8">
            {/* New Project Card + Quota Bar */}
            {canCreateProject && (
              <div className="flex flex-col gap-4">
                <NewProjectCard onClick={handleCreateTeam} />
                {subscriptionInfo && (
                  <QuotaBar
                    plan={subscriptionInfo.plan}
                    projectsUsed={subscriptionInfo.projectsUsed}
                  />
                )}
              </div>
            )}

            {/* In Progress Section */}
            <InProgressSection teams={inProgressTeams} />

            {/* Completed Section */}
            <CompletedSection teams={completedTeams} />
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
